import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Wordmark } from '../components';
import { ArrowLeft, Briefcase, Users, Building2, TrendingUp, Minus, Tag } from 'lucide-react';

const pricingData = {
  gov: {
    small:  { eb:[50,95],   ga:[95,175],   vip:[175,350],  sponsor:[350,750]   },
    mid:    { eb:[75,150],  ga:[150,275],  vip:[275,550],  sponsor:[550,1200]  },
    large:  { eb:[95,200],  ga:[200,400],  vip:[400,850],  sponsor:[850,2500]  }
  },
  corp: {
    small:  { eb:[199,399],  ga:[399,699],   vip:[699,1200],  sponsor:[1200,3000]   },
    mid:    { eb:[299,599],  ga:[599,1099],  vip:[1099,2000], sponsor:[2000,5000]   },
    large:  { eb:[499,999],  ga:[999,1799],  vip:[1799,3500], sponsor:[3500,10000]  }
  },
  consumer: {
    small:  { eb:[15,35],   ga:[35,75],    vip:[75,150],   sponsor:[150,500]   },
    mid:    { eb:[25,60],   ga:[60,120],   vip:[120,275],  sponsor:[275,1000]  },
    large:  { eb:[40,99],   ga:[99,199],   vip:[199,500],  sponsor:[500,2500]  }
  }
};

const tierMeta = {
  eb:      { label: 'Early Bird',        color: '#7ab8f5' },
  ga:      { label: 'General Admission', color: '#b5a8f0' },
  vip:     { label: 'VIP / Premium',     color: '#2a7dd4' },
  sponsor: { label: 'Sponsor / Table',   color: '#7c6fd4' }
};

const typeLabels = {
  gov:      'Government / Federal',
  corp:     'Corporate / Conference',
  consumer: 'Consumer / Public'
};

const typeIcons = {
  gov: Building2,
  corp: Briefcase,
  consumer: Users
};

const sizeLabels = {
  small: 'Small (<500)',
  mid:   'Mid (500–2,500)',
  large: 'Large (2,500+)'
};

function getPricingSignal(days, type) {
  if (days >= 90) {
    return {
      signal: 'raise',
      title: 'Launch Early Bird pricing',
      reason: "You're 90+ days out — the optimal window to capture demand with discounted early access. Industry data shows early bird tiers sell 28–40% of total capacity at higher conversion rates than any other phase.",
      tip: 'Tip: Set early bird pricing 20–30% below GA. Close this tier at 75–90 days out to build urgency and protect margin.'
    };
  } else if (days >= 60) {
    return {
      signal: 'raise',
      title: 'Transition to standard pricing',
      reason: "The early bird window is closing. Raise prices to your GA rate now — PCMA data shows the 60–89 day window delivers the highest revenue-per-ticket for professional and government events.",
      tip: 'Tip: Announce the price increase 7 days before it takes effect to drive a final surge of early registrations.'
    };
  } else if (days >= 30) {
    return {
      signal: 'hold',
      title: type === 'consumer' ? 'Hold — or a slight increase' : 'Hold pricing steady',
      reason: "You're in the core sales window. Cvent benchmarks show stable pricing at 30–59 days out maximizes total yield. Discounting now signals low demand and erodes perceived value among B2B buyers.",
      tip: 'Tip: Use this window for targeted outreach — group rates, team registrations, and referral programs rather than price cuts.'
    };
  } else if (days >= 14) {
    return {
      signal: 'raise',
      title: 'Last call — increase prices now',
      reason: "Last-call pricing (14–29 days) consistently yields 12–22% higher per-ticket revenue across all event categories. Remaining capacity is scarce and time pressure drives conversion at premium prices.",
      tip: 'Tip: Add a countdown timer to your registration page. Urgency converts more reliably than discounts at this stage.'
    };
  } else if (days >= 7) {
    return type === 'consumer'
      ? {
          signal: 'discount',
          title: 'Selective discounting',
          reason: "Consumer events can deploy targeted flash discounts (15–20%) in the final week to fill remaining capacity without devaluing the brand. Limit to a single promo code broadcast — don't stack offers.",
          tip: "Tip: Frame it as a 'final weekend offer,' not a sale. Scarcity language outperforms percentage-off messaging by 2:1."
        }
      : {
          signal: 'hold',
          title: 'Hold — or bundle instead',
          reason: "Professional and government events should hold pricing in the final 1–2 weeks. Late discounting signals poor demand to B2B buyers and may reduce future early-registration rates.",
          tip: 'Tip: Offer added value instead — workshop access, a roundtable seat, or digital content at the existing price point.'
        };
  } else {
    return {
      signal: 'raise',
      title: 'Door / surge pricing',
      reason: "On-site and final-day registration should carry your highest price. Eventbrite data shows an 18–35% premium over standard rates is accepted by last-minute registrants across all event types.",
      tip: 'Tip: Publish your door price publicly in advance — price transparency builds trust and drives pre-event conversions before the door rate kicks in.'
    };
  }
}

export function Playbook() {
  const [eventType, setEventType] = useState('gov');
  const [eventSize, setEventSize] = useState('small');
  const [daysOut, setDaysOut] = useState(90);

  const currentData = pricingData[eventType][eventSize];
  const maxPrice = Math.max(...Object.values(currentData).map(r => r[1]));
  
  const signal = useMemo(() => getPricingSignal(daysOut, eventType), [daysOut, eventType]);

  const signalColors = {
    raise: { bg: '#fee2e2', border: '#ef4444', text: '#dc2626' },
    hold: { bg: '#fef3c7', border: '#f59e0b', text: '#d97706' },
    discount: { bg: '#dcfce7', border: '#22c55e', text: '#16a34a' }
  };

  const SignalIcon = signal.signal === 'raise' ? TrendingUp : signal.signal === 'discount' ? Tag : Minus;
  const colors = signalColors[signal.signal];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Nav */}
      <nav style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 100,
        padding: '16px 24px',
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--line)'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <a href="/" style={{ textDecoration: 'none' }}>
            <Wordmark size={28} />
          </a>
          <a 
            href="/organizers" 
            style={{ 
              fontSize: 14, 
              color: 'var(--ink-2)', 
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <ArrowLeft size={16} />
            Back to For Organizers
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ padding: '140px 24px 60px', textAlign: 'center' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <span style={{ 
            display: 'inline-block', 
            padding: '6px 14px', 
            background: 'var(--primary-tint)', 
            color: 'var(--primary)', 
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: 24
          }}>
            Promotion Playbook
          </span>
          
          <h1 style={{ 
            fontSize: 'clamp(32px, 5vw, 48px)', 
            fontWeight: 600, 
            lineHeight: 1.1, 
            marginBottom: 20,
            color: 'var(--ink)'
          }}>
            Ticket Price<br />
            <span style={{ color: 'var(--primary)' }}>Optimizer</span>
          </h1>

          <p style={{ 
            fontSize: 'clamp(17px, 2.5vw, 20px)', 
            color: 'var(--ink-2)', 
            maxWidth: 560, 
            margin: '0 auto',
            lineHeight: 1.6
          }}>
            Data-backed pricing recommendations for your event. Select your event type, size, and timeline to get general industry-standard benchmarks.
          </p>
                 </div>  
      </section>
          
      {/* Tool */}
      <section style={{ padding: '40px 24px 80px' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          
          <h4 style={{ fontSize: 14, color: 'var(--ink-3)', textAlign: 'center', marginBottom: 16 }}>Information listed below should be leveraged as insight and not as final recommendations. Organizers should always consider their specific circumstances and market conditions.</h4>

          {/* Step 1: Event Type & Size */}
          <div style={{
            background: 'var(--bg-2)',
            borderRadius: 16,
            border: '1px solid var(--line)',
            padding: '24px 28px',
            marginBottom: 16
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
              padding: '4px 12px',
              background: 'var(--bg)',
              borderRadius: 999,
              marginBottom: 16
            }}>
              Step 1
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4, color: 'var(--ink)' }}>
              Event Profile
            </h2>
            <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 20 }}>
              Select your event type and size to get relevant benchmarks
            </p>

            {/* Event Type */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
              {Object.entries(typeLabels).map(([key, label]) => {
                const Icon = typeIcons[key];
                const isActive = eventType === key;
                return (
                  <button
                    key={key}
                    onClick={() => setEventType(key)}
                    style={{
                      padding: '14px 12px',
                      background: isActive ? 'var(--primary-tint)' : 'var(--bg)',
                      border: `1px solid ${isActive ? 'var(--primary)' : 'var(--line)'}`,
                      borderRadius: 12,
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.18s ease'
                    }}
                  >
                    <Icon size={20} style={{ 
                      color: isActive ? 'var(--primary)' : 'var(--ink-3)', 
                      marginBottom: 6,
                      display: 'block'
                    }} />
                    <span style={{ 
                      fontSize: 13, 
                      fontWeight: 600, 
                      color: 'var(--ink)',
                      display: 'block'
                    }}>
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Event Size */}
            <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--ink-3)', marginBottom: 8 }}>
              Event Size
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Object.entries(sizeLabels).map(([key, label]) => {
                const isActive = eventSize === key;
                return (
                  <button
                    key={key}
                    onClick={() => setEventSize(key)}
                    style={{
                      padding: '8px 18px',
                      background: isActive ? '#ede9fb' : 'var(--bg)',
                      border: `1px solid ${isActive ? '#7c6fd4' : 'var(--line)'}`,
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? '#7c6fd4' : 'var(--ink-2)',
                      transition: 'all 0.18s ease'
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2: Benchmarks */}
          <div style={{
            background: 'var(--bg-2)',
            borderRadius: 16,
            border: '1px solid var(--line)',
            padding: '24px 28px',
            marginBottom: 16
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
              padding: '4px 12px',
              background: 'var(--bg)',
              borderRadius: 999,
              marginBottom: 16
            }}>
              Step 2
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4, color: 'var(--ink)' }}>
              {typeLabels[eventType]} — {sizeLabels[eventSize]} Benchmarks
            </h2>
            <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 20 }}>
              Industry-standard ranges by ticket tier · Source: PCMA Convene, Cvent, Eventbrite (2023–2024)
            </p>

            {/* Tier Bars */}
            <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
              {Object.entries(currentData).map(([tier, range]) => {
                const meta = tierMeta[tier];
                const pct = Math.round((range[1] / maxPrice) * 100);
                return (
                  <div key={tier} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <span style={{ fontSize: 13, color: 'var(--ink-2)', width: 130, flexShrink: 0, fontWeight: 500 }}>
                      {meta.label}
                    </span>
                    <div style={{ flex: 1, height: 8, background: 'var(--bg)', borderRadius: 999, overflow: 'hidden', border: '1px solid var(--line)' }}>
                      <div style={{ 
                        height: '100%', 
                        borderRadius: 999, 
                        width: `${pct}%`, 
                        background: meta.color,
                        transition: 'width 0.45s ease'
                      }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', width: 130, textAlign: 'right', flexShrink: 0 }}>
                      ${range[0].toLocaleString()} – ${range[1].toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Timeline Phases */}
            <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', marginTop: 20, border: '1px solid var(--line)' }}>
              {[
                { label: 'Early Bird', sub: '180–90 days', bg: '#e8f4ff', color: '#1a5fa8' },
                { label: 'Standard', sub: '89–45 days', bg: '#ede9fb', color: '#7c6fd4' },
                { label: 'Last Call', sub: '44–14 days', bg: '#fff4e0', color: '#a05c00' },
                { label: 'Door / Surge', sub: '13–0 days', bg: '#fde8e8', color: '#b02020' }
              ].map((phase, i) => (
                <div key={i} style={{ flex: 1, padding: '8px 10px', fontSize: 11, textAlign: 'center', background: phase.bg }}>
                  <span style={{ fontWeight: 700, display: 'block', color: phase.color }}>{phase.label}</span>
                  <span style={{ opacity: 0.75, display: 'block', marginTop: 2, color: phase.color }}>{phase.sub}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Step 3: Pricing Signal */}
          <div style={{
            background: 'var(--bg-2)',
            borderRadius: 16,
            border: '1px solid var(--line)',
            padding: '24px 28px'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
              padding: '4px 12px',
              background: 'var(--bg)',
              borderRadius: 999,
              marginBottom: 16
            }}>
              Step 3
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4, color: 'var(--ink)' }}>
              Pricing Signal
            </h2>
            <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 20 }}>
              Drag the slider to your current days-to-event to get a data-backed recommendation
            </p>

            {/* Slider */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>Days until your event</span>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>
                  <span>{daysOut}</span>
                  <span style={{ fontSize: 13, color: 'var(--ink-2)', marginLeft: 4 }}>days out</span>
                </div>
              </div>
              <input
                type="range"
                min="0"
                max="180"
                value={daysOut}
                onChange={(e) => setDaysOut(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  height: 6,
                  borderRadius: 3,
                  background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${(daysOut / 180) * 100}%, var(--bg) ${(daysOut / 180) * 100}%, var(--bg) 100%)`,
                  outline: 'none',
                  cursor: 'pointer',
                  appearance: 'none',
                  WebkitAppearance: 'none'
                }}
              />
            </div>

            {/* Signal Display */}
            <div 
              role="region"
              aria-label="Pricing recommendation"
              style={{
                padding: 20,
                background: colors.bg,
                borderRadius: 12,
                border: `1px solid ${colors.border}`
              }}
            >
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: colors.border,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <SignalIcon size={20} style={{ color: colors.text }} />
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: colors.text, marginBottom: 4 }}>
                    {signal.title}
                  </div>
                </div>
              </div>
              <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, marginBottom: 12 }}>
                {signal.reason}
              </p>
              <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '8px 12px', background: 'var(--bg)', borderRadius: 6 }}>
                {signal.tip}
              </div>
            </div>
          </div>

          {/* Source Note */}
          <p style={{ fontSize: 13, color: 'var(--ink-4)', textAlign: 'center', marginTop: 24 }}>
            Benchmark data sourced from PCMA Convene Meetings Outlook, Cvent Event Industry Trends Report,<br />
            and Eventbrite Pulse: The State of Event Ticketing (2023–2024 editions)
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: '40px 24px', borderTop: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
          <Wordmark size={20} />
          <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
            <Link to="/about" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>About</Link>
            <Link to="/privacy" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>Privacy</Link>
            <Link to="/terms" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>Terms</Link>
            <a href="mailto:info@VentyVIP.com" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>Contact</a>
          </div>
          <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>© 2026 VentyVIP</span>
        </div>
      </footer>
    </div>
  );
}
